import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import {
  PageHeader,
  SectionHeader,
  Kpi,
  Card,
  RangeSelect,
  IncludeTestToggle,
  TestDataBanner,
  useIncludeTest,
  centsToUsd,
} from "./adminUi";

/** Subscriptions page — executive-friendly view of the paid LifeGuide programme.
 *  Uses `/admin/revenue` and `/admin/master-overview` — no raw Stripe secrets.
 */
export default function AdminSubscriptionsPage() {
  const [includeTest, setIncludeTest] = useIncludeTest();
  const [range, setRange] = useState("30d");
  const [rev, setRev] = useState(null);
  const [ov, setOv] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setRev(null);
    setOv(null);
    Promise.all([
      adminApi.revenue({ range, include_test: includeTest }),
      adminApi.masterOverview({ include_test: includeTest }),
    ])
      .then(([r, o]) => {
        setRev(r);
        setOv(o);
      })
      .catch((e) =>
        setErr(e?.response?.data?.detail || "Unable to load subscriptions")
      );
  }, [includeTest, range]);

  const subs = rev?.subscriptions || {};
  const lg = ov?.lifeguide || {};

  return (
    <div data-testid="admin-subscriptions-page" className="space-y-8">
      <PageHeader
        eyebrow="Programme"
        title="Subscriptions"
        subtitle="LifeGuide Pro — currently paying members, trials, cancellations, and monthly recurring revenue."
        right={
          <div className="flex items-center gap-4">
            <RangeSelect value={range} onChange={setRange} />
            <IncludeTestToggle value={includeTest} onChange={setIncludeTest} />
          </div>
        }
      />
      <TestDataBanner show={includeTest} />

      {err && (
        <div className="rounded-2xl border border-[#B4795C]/50 bg-[#241914] text-[#F3D6A6] p-4 text-sm">
          {err}
        </div>
      )}

      <section>
        <SectionHeader label="Live programme" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="sub-current-pro" label="Current Pro subscribers" value={lg.paid_pro} tone="accent" />
          <Kpi testid="sub-active-trials" label="Users in 3-day trial" value={lg.trial_users} />
          <Kpi
            testid="sub-trial-conv"
            label="Trial → Paid"
            value={lg.trial_to_paid_pct != null ? `${lg.trial_to_paid_pct}%` : "—"}
          />
          <Kpi testid="sub-cancellations" label="Cancellations — 30d" value={lg.cancellations_30d} />
          <Kpi
            testid="sub-mrr"
            label="Estimated MRR"
            value={centsToUsd(lg.mrr_cents)}
            tone="accent"
            hint="Per-subscription (webhook-maintained)"
          />
          <Kpi testid="sub-new" label="New subscriptions" value={subs.new ?? "—"} />
          <Kpi testid="sub-canceled" label="Canceled in range" value={subs.canceled ?? "—"} />
          <Kpi testid="sub-gift-seats" label="Gift-granted seats" value={lg.gift_subscriptions} />
        </div>
      </section>

      <section>
        <SectionHeader label="Notes" />
        <Card className="text-sm text-[#B7D2AF] leading-relaxed">
          Payment credentials are never exposed on this dashboard. Stripe
          secret keys, customer payment methods, and webhook secrets are
          managed only through the Emergent platform's Secrets area.
        </Card>
      </section>
    </div>
  );
}
